import { useQuery } from '@apollo/client';
import { makeDataErrorMessages } from '@atlassian/amkt-common';
import { getScopesLookup, getSiteDetails } from '@atlassian/mpac-api-client';
import { DEFAULT_CURRENCY_CODE } from '@atlassian/mpac-pricing-utils';
import { AppPricing, GetHostStatus, GetInstalledAppDetails, StargateAPI } from '@atlassian/mpac-services-client';
import { AtlassianProductHostingType } from '@atlassian/mpac-types';
import { MarketplaceStoreInstallationTargetProduct } from '@atlassian/mpac-types/graphql-gateway-types';
import { hooks } from '@atlassian/mpac-utils';
import { Either } from 'funfix-core';
import { useState } from 'react';
export const useAvailableSites = (stargateBaseUrl, products) => {
  const availableSitesPromise = () => new StargateAPI(stargateBaseUrl).marketplace.availableSites(products);
  const {
    result,
    error,
    pending
  } = hooks.usePromise(availableSitesPromise, [products, stargateBaseUrl]);
  return {
    availableSitesPending: pending,
    availableSitesError: error,
    availableSitesResult: result === null || result === void 0 ? void 0 : result.sites
  };
};
export const useSelectedSiteDetails = (mpacBaseUrl, selectedSiteCloudId) => {
  const selectedSiteDetailsPromise = () => getSiteDetails(mpacBaseUrl, selectedSiteCloudId);
  const {
    result,
    error,
    pending
  } = hooks.usePromise(selectedSiteDetailsPromise, [mpacBaseUrl, selectedSiteCloudId]);
  return {
    selectedSiteDetailsPending: pending,
    selectedSiteDetailsError: error,
    selectedSiteDetailsResult: result
  };
};
export const useAppInstallPermission = (stargateBaseUrl, product, cloudId) => {
  const appInstallPermissionPromise = product && cloudId ? new StargateAPI(stargateBaseUrl).marketplace.appInstallPermission(product, cloudId) : new Promise(() => {
    /* empty promise, never resolves */
  });
  const {
    result,
    error,
    pending
  } = hooks.usePromise(() => appInstallPermissionPromise, [product, cloudId], !!(product && cloudId));
  return {
    appInstallPermissionPending: pending,
    appInstallPermissionError: error,
    appInstallPermissionResult: result
  };
};
const ProductNameToTargetProductMap = {
  confluence: MarketplaceStoreInstallationTargetProduct.Confluence,
  jira: MarketplaceStoreInstallationTargetProduct.Jira,
  compass: MarketplaceStoreInstallationTargetProduct.Compass
};
export const useOneClickInstallSiteSelector = (appKey, appCompatibilities, stargateBasePath) => {
  const [selectedSiteAndProduct, setSelectedSiteAndProduct] = useState();
  const availableSitesPromise = useAvailableSites(stargateBasePath, appCompatibilities);
  const appInstallPermissionPromise = useAppInstallPermission(stargateBasePath, selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.product, selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.site.cloudId);

  // since we are skipping query if site or product is not selected, it is safe to assume that
  // site.cloudId and product will always be defined
  const installAppTargetInput = {
    cloudId: selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.site.cloudId,
    product: ProductNameToTargetProductMap[selectedSiteAndProduct === null || selectedSiteAndProduct === void 0 ? void 0 : selectedSiteAndProduct.product]
  };
  const skipQuery = !selectedSiteAndProduct || !selectedSiteAndProduct.product || !selectedSiteAndProduct.site.cloudId;
  const {
    data: installedAppDetailsResult,
    loading: installedAppDetailsLoading,
    error: installedAppDetailsError
  } = useQuery(GetInstalledAppDetails, {
    variables: {
      input: {
        appKey,
        target: installAppTargetInput
      }
    },
    skip: skipQuery
  });
  const {
    data: hostStatusResult,
    loading: hostStatusLoading,
    error: hostStatusError
  } = useQuery(GetHostStatus, {
    variables: {
      input: installAppTargetInput
    },
    skip: skipQuery
  });
  return {
    selectedSiteAndProduct,
    availableSitesPromise,
    appInstallPermissionPromise,
    installedAppDetailsPromise: {
      installedAppDetailsResult,
      installedAppDetailsLoading,
      installedAppDetailsError
    },
    hostStatusPromise: {
      hostStatusResult,
      hostStatusLoading,
      hostStatusError
    },
    onSiteChange: siteProductSelected => setSelectedSiteAndProduct(siteProductSelected)
  };
};
export const useScopesLookup = graphQLGatewayUrl => {
  var _error$message;
  const scopesLookupPromise = () => getScopesLookup(graphQLGatewayUrl);
  const {
    result,
    error
  } = hooks.usePromise(scopesLookupPromise, [graphQLGatewayUrl]);
  return {
    scopesLookup: result ? Either.right(result) : Either.left(makeDataErrorMessages([(_error$message = error === null || error === void 0 ? void 0 : error.message) !== null && _error$message !== void 0 ? _error$message : 'Failed to fetch scope lookups']))
  };
};
export const usePricingPlan = (appId, billingCurrency) => {
  const {
    data: pricingPlanResult,
    error: pricingPlanError,
    loading: pricingPlanPending
  } = useQuery(AppPricing, {
    variables: {
      appId,
      hosting: AtlassianProductHostingType.Cloud,
      countryCode: billingCurrency !== null && billingCurrency !== void 0 ? billingCurrency : DEFAULT_CURRENCY_CODE
    }
  });
  return {
    pricingPlanResult,
    pricingPlanError,
    pricingPlanPending
  };
};